import { Box, CircularProgress, Container, Dialog, DialogContent, Grid, Modal, Step, StepButton, Stepper, Tab, Tabs } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { USER_TYPES } from '../../config/dataService/constants';
import { inputFormatPhoneNumber, removeDashFromPhoneNumber } from '../../container/Common/FormatPhoneNumber';
import {
    customerKeys,
    useCheckPhoneNumber,
    useCreateCustomer,
    useDeleteCustomer,
    useDeleteVehicle,
    useGetCreatedCustomer,
    useGetImportedCustomer,
    useGetVehicleListByUserId,
    useUpdateCustomer,
    useUpdateCustomerImported,
    vehicleKeys,
} from '../../services/business/customer';
import { useGetCityMasterList, useGetCountryMasterList, useGetProvinceMasterList, useGetVehicleYearMasterList } from '../../services/core/master/master.hooks';
import { useGetShopConfig } from '../../services/core/settings';
import { useGetDiscountPrograms } from '../../services/core/settings/settings.hooks';
import { useDeleteImportedVehicle } from '../../services/core/vehicles/vehicle.hooks';
import { masterQueryOptions } from '../../utility/Constant';
import { filterUndefined, formatPostalCode, getPostalCodeError, isPostalCodeValid, validateVehicle } from '../../utility/helpers/commonFunction';
import CommonDialog from '../CommonDialog';
import VehicleModalV4 from '../VehicleModalV4';
import AdditionalDetailsForm from './AdditionalDetailsForm';
import CustomerDetailsForm from './CustomerDetailsForm';
import ModalFooter from './ModalFooter';
import ModalHeader from './ModalHeader';
import VehicleDetailsForm from './VehicleDetailsForm';
import VehicleSelectionForm from './VehicleSelectionForm';

// Consolidated Style Object
const styles = {
    modalBox: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        boxShadow: 24,
        border: 'none',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    container: {
        marginTop: '66px',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    gridContainer: { scrollbarColor: 'rgba(0,0,0,0.2)', scrollbarWidth: 'thin', flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px' },
    stepperContainer: {
        maxWidth: '600px',
        width: '100%',
        height: '48px',
        '& .MuiStepLabel-root .MuiStepLabel-iconContainer': {
            paddingRight: '8px',
        },
        // Disabled/Inactive step styling
        '& .MuiStep-root:not(.Mui-completed) .MuiStepIcon-root:not(.Mui-active)': {
            color: '#9B9C9E', // Gray background for disabled steps
            '& .MuiStepIcon-text': {
                backgroundColor: '#9B9C9E',
                fill: '#fff', // White number text
            },
        },
        // Disabled/Inactive step label text
        '& .MuiStep-root:not(.Mui-completed) .MuiStepLabel-label:not(.Mui-active)': {
            color: '#9B9C9E', // Gray text for disabled steps
            fontWeight: 500,
            lineHeight: '150%',
        },
        // Active step styling (blue)
        '& .MuiStepIcon-root.Mui-active': {
            color: '#1976d2', // Blue background for active step
            fontWeight: 500,
            lineHeight: '150%',
            '& .MuiStepIcon-text': {
                fill: '#fff', // White number text
            },
        },
        // Active step label text
        '& .MuiStepLabel-label.Mui-active': {
            color: '#212121', // Dark text for active step
            fontWeight: 500,
            lineHeight: '150%',
        },
        // Completed step styling (blue with checkmark)
        '& .MuiStepIcon-root.Mui-completed': {
            color: '#1976d2', // Blue background for completed steps
        },
        // Completed step label text
        '& .MuiStepLabel-label.Mui-completed': {
            color: '#212121', // Dark text for completed steps
            fontWeight: 500,
            lineHeight: '150%',
        },
        // Connector line styling
        '& .MuiStepConnector-line': {
            borderColor: '#E0E0E0',
        },
    },
};

const AddCustomerModalV4 = ({
    from = 'profile',
    openModal,
    onClose,
    onSubmitModal,
    openEditCustomer,
    customerType = USER_TYPES.SHOP_CREATED,
    customerId = null,
    fromProfile = false,
}) => {
    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setValue,
        setError,
        getValues,
        setFocus,
        watch,
        clearErrors,
        trigger,
        getFieldState,
    } = useForm({
        defaultValues: {
            membershipProgram: 'none',
            isFleetCustomer: false,
            primaryPhoneNumberType: 'mobile',
            secondaryPhoneNumberType: 'mobile',
            taxExemptionNumber: '',
        },
    });
    const queryClient = useQueryClient();
    const phoneInputRef = useRef(null);
    const emailRef = useRef(null);
    const isEditCustomerMode = !!customerId && (customerType === USER_TYPES.SHOP_CREATED || customerType === USER_TYPES.APEX_AUTO_APP);
    const isApexAutoUser = customerType === USER_TYPES.APEX_AUTO_APP;

    const steps = ['Customer Details', 'Additional Details', 'Vehicle Details'];

    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState({});
    const [sendWelcomeEmailToCustomer, setSendWelcomeEmailToCustomer] = useState(true);
    const [triggerSelection, setTriggerSelection] = useState(null);
    const [selectedPhoneTypes, setSelectedPhoneTypes] = useState({
        primary: 'mobile',
        secondary: 'mobile',
    });
    const [recordFoundByPhone, setRecordFoundByPhone] = useState({
        id: '',
        found: false,
        isFleetCustomer: false,
        isShopImported: false,
    });

    const [showCustomerFoundMessage, setShowCustomerFoundMessage] = useState(false);

    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedCountrySecondary, setSelectedCountrySecondary] = useState(null);

    const [isAddingNewVehicle, setIsAddingNewVehicle] = useState(false);
    const [editVehicleData, setEditVehicleData] = useState(null);
    const [deletevehicleData, setDeleteVehicleData] = useState(null);
    const [isDeleteModal, setIsDeleteModal] = useState(false);
    const [isCustomerDeleteModal, setIsCustomerDeleteModal] = useState(false);
    const [customerVehicles, setCustomerVehicles] = useState([]);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);

    const buildSelectedVehicleMeta = useCallback(() => {
        if (!selectedVehicleId || !Array.isArray(customerVehicles) || customerVehicles.length === 0) {
            return null;
        }
        const vehicle = customerVehicles.find((v) => v?._id === selectedVehicleId);
        if (!vehicle) {
            return null;
        }

        const normalize = (val) => (val === undefined || val === null ? '' : val);

        return {
            id: vehicle._id || null,
            vin: normalize(vehicle.vin),
            licenseNumber: normalize(vehicle.licenseNumber || vehicle.license),
            provinceCode: normalize(vehicle.provinceCode || vehicle.province?.code || vehicle.province?.provinceCode),
            year: vehicle.year || vehicle.vehicleYear || null,
            makeId: vehicle.vehicleMake?._id || vehicle.vehicleMakeId || null,
            modelId: vehicle.vehicleModel?._id || vehicle.vehicleModelId || null,
            trimId: vehicle.vehicleModelTrim?._id || vehicle.vehicleModelTrimId || null,
            fleetUnitNumber: normalize(vehicle.fleetUnitNumber),
        };
    }, [selectedVehicleId, customerVehicles]);

    const shopCountryId = localStorage.getItem('shopCountryId') || '64e738882fec8f2c9b79c139';

    // TanStack Query for country list
    const { data: countryList, isLoading: countryListLoader } = useGetCountryMasterList(
        { countryId: shopCountryId },
        { enabled: !!shopCountryId, ...masterQueryOptions }
    );

    // TanStack Query for city list
    const {
        data: cityList,
        isLoading: cityListLoader,
        refetch: fetchCityList,
    } = useGetCityMasterList({ countryId: selectedCountry?._id }, { enabled: !!selectedCountry?._id, ...masterQueryOptions });

    // TanStack Query for province list
    const { data: provinceList, isLoading: provinceListLoader } = useGetProvinceMasterList(
        { countryId: selectedCountry?._id },
        { enabled: !!selectedCountry?._id, ...masterQueryOptions }
    );

    // TanStack Query for vehicle year list
    const { data: vehicleYearList, isLoading: vehicleYearListLoader } = useGetVehicleYearMasterList({
        page: 1,
        limit: 1000,
    });

    // TanStack Query for combined province list
    const { data: provinceCombinedList, isLoading: provinceCombinedListLoader } = useGetProvinceMasterList({}, { ...masterQueryOptions });

    // TanStack Query for shop config
    const { data: shopConfigData, isLoading: isConfigLoading, error: configError } = useGetShopConfig();

    // Hydrate sendWelcomeEmailToCustomer from shop config API
    useEffect(() => {
        const config = shopConfigData?.config || shopConfigData;
        if (config && config.defaultSendWelcomeEmailToCustomer !== undefined) {
            setSendWelcomeEmailToCustomer(Boolean(config.defaultSendWelcomeEmailToCustomer));
        }
    }, [shopConfigData]);

    // TanStack Query for discount programs
    const { data: discountList, isLoading: discountProgramLoader } = useGetDiscountPrograms({ pagination: false });

    const {
        data: customerDetailData,
        isLoading: customerDetailLoading,
        isError: isCustomerDetailError,
        error: customerDetailError,
    } = useGetCreatedCustomer(customerId, {
        enabled: openModal && (customerType === USER_TYPES.SHOP_CREATED || customerType === USER_TYPES.APEX_AUTO_APP) && !!customerId,
        staleTime: 0,
        cacheTime: 0,
        gcTime: 0,
    });

    const {
        data: shopImportedCustomerDetail,
        isLoading: shopImportedCustomerDetailLoading,
        isError: isshopImportedCustomerDetailError,
        error: shopImportedCustomerDetailError,
    } = useGetImportedCustomer(customerId, {
        enabled: openModal && (customerType === USER_TYPES.SHOP_IMPORTED || customerType === USER_TYPES.WALK_IN) && !!customerId,
        staleTime: 0,
        cacheTime: 0,
        gcTime: 0,
    });

    // Customer Mutations
    const checkPhoneMutation = useCheckPhoneNumber({
        onSuccess: (data) => {
            if (data?.status && data?.data) {
                // Customer found with this phone number
                const customer = data.data;
                setRecordFoundByPhone({
                    id: customer._id,
                    found: true,
                    isFleetCustomer: customer?.isFleetCustomer || false,
                    isShopImported: customer?.shopImported || false,
                });

                if (customer?.vehicleData?.length > 0) {
                    setTimeout(() => {
                        setCustomerVehicles(customer.vehicleData);
                    }, 1000);
                }

                // Pre-fill basic info
                if (customer.firstName) setValue('firstName', customer.firstName);
                if (customer.lastName) setValue('lastName', customer.lastName);
                if (customer.company) setValue('company', customer.company);

                // Pre-fill primary email
                if (customer.email) setValue('email', customer.email);

                // Pre-fill secondary email
                if (customer.secondaryEmail) setValue('secondaryEmail', customer.secondaryEmail);

                // Pre-fill primary phone
                const primaryPhoneType = customer.primaryPhoneNumberType || 'mobile';
                setValue('primaryPhoneNumberType', primaryPhoneType);
                setSelectedPhoneTypes((prev) => ({ ...prev, primary: primaryPhoneType }));

                // Secondary phone
                if (customer.secondaryPhoneNumber) {
                    const formatted = customer.secondaryPhoneNumber.includes('-')
                        ? customer.secondaryPhoneNumber
                        : inputFormatPhoneNumber(customer.secondaryPhoneNumber);
                    setValue('secondaryPhoneNumber', formatted);

                    const secondaryPhoneType = customer.secondaryPhoneNumberType || 'mobile';
                    setValue('secondaryPhoneNumberType', secondaryPhoneType);
                    setSelectedPhoneTypes((prev) => ({ ...prev, secondary: secondaryPhoneType }));
                }

                // Pre-fill contact preference
                if (customer.contactPrefrence) {
                    setValue('contactPrefrence', customer.contactPrefrence);
                }

                // Pre-fill address
                if (customer.streetAddress) setValue('streetAddress', customer.streetAddress);
                if (customer.city) setValue('city', customer?.city?._id || customer?.city);
                if (customer.province) setValue('province', customer.province?._id || customer?.province);
                if (customer.postalCode) setValue('postalCode', formatPostalCode(customer.postalCode, customer?.country || selectedCountry));
                if (customer.taxExemptionNumber) setValue('taxExemptionNumber', customer.taxExemptionNumber);

                // Pre-fill membership program
                if (customer?.membershipProgram?._id) {
                    setValue('membershipProgram', customer?.membershipProgram?._id || customer?.membershipProgram);
                } else {
                    setValue('membershipProgram', 'none');
                }

                // Pre-fill fleet customer status
                if (customer.isFleetCustomer) {
                    setValue('isFleetCustomer', true);
                }

                // Show info message below phone field instead of toast
                setShowCustomerFoundMessage(true);
            } else {
                // No customer found - hide message but keep data
                if (shopImportedCustomerDetail?._id) {
                    setRecordFoundByPhone({
                        id: shopImportedCustomerDetail._id,
                        found: true,
                        isFleetCustomer: shopImportedCustomerDetail.isFleetCustomer,
                        isShopImported: true,
                    });
                } else {
                    setRecordFoundByPhone({
                        id: '',
                        found: false,
                        isFleetCustomer: false,
                        isShopImported: false,
                    });
                }
                setShowCustomerFoundMessage(false);
            }
        },
        onError: (error) => {
            if (shopImportedCustomerDetail?._id) {
                setRecordFoundByPhone({
                    id: shopImportedCustomerDetail._id,
                    found: true,
                    isFleetCustomer: shopImportedCustomerDetail.isFleetCustomer,
                    isShopImported: true,
                });
            } else {
                setRecordFoundByPhone({
                    id: '',
                    found: false,
                    isFleetCustomer: false,
                    isShopImported: false,
                });
            }
            setShowCustomerFoundMessage(false);
        },
    });

    const createCustomerMutation = useCreateCustomer({
        onSuccess: (data) => {
            if (onSubmitModal) {
                const selectedVehicleMeta = buildSelectedVehicleMeta();
                onSubmitModal({ ...data, selectedVehicleId, selectedVehicle: selectedVehicleMeta });
            }
            handleCloseModal();
        },
    });

    const updateCustomerMutation = useUpdateCustomer({
        onSuccess: (data) => {
            if (onSubmitModal) {
                const selectedVehicleMeta = buildSelectedVehicleMeta();
                onSubmitModal({ ...data, selectedVehicleId, selectedVehicle: selectedVehicleMeta });
            }
            handleCloseModal();
        },
    });

    const updateCustomerImportedMutation = useUpdateCustomerImported({
        onSuccess: (data, variables) => {
            if (onSubmitModal) {
                // Use selectedVehicleId from variables (submit-time value) to avoid stale closure for shop_imported record-found flow
                const vehicleId = variables?.selectedVehicleId ?? selectedVehicleId;
                const selectedVehicleMeta = buildSelectedVehicleMeta();
                onSubmitModal({ ...data, selectedVehicleId: vehicleId, selectedVehicle: selectedVehicleMeta });
            }
            handleCloseModal();
        },
    });

    const deleteVehicleMutation = useDeleteVehicle({
        onSuccess: () => {
            toast.success('Vehicle deleted successfully');
            setIsDeleteModal(false);
            setDeleteVehicleData(null);
            refetchVehicles();
        },
        onError: () => {
            setIsDeleteModal(false);
            setDeleteVehicleData(null);

            toast.error('Failed to delete vehicle');
        },
    });

    const deleteImportedVehicleMutation = useDeleteImportedVehicle({
        onSuccess: (data, variables) => {
            toast.success('Vehicle deleted successfully');

            // Manually update local state for shop imported vehicles or customers
            if (recordFoundByPhone?.isShopImported || (deletevehicleData?.source === 'shop_imported' && !deletevehicleData?.isConverted)) {
                setCustomerVehicles((prev) => prev.filter((v) => v._id !== variables));
            } else {
                refetchVehicles();
            }

            setIsDeleteModal(false);
            setDeleteVehicleData(null);
        },
        onError: () => {
            setIsDeleteModal(false);
            setDeleteVehicleData(null);
            toast.error('Failed to delete vehicle');
        },
    });

    const deleteCustomerMutation = useDeleteCustomer({
        onSuccess: (data) => {
            if (onSubmitModal) {
                onSubmitModal(data, true);
            }
            handleCloseModal();
        },
    });

    // Fetch vehicles when customer is found
    const {
        data: vehicleListData,
        refetch: refetchVehicles,
        isLoading: isVehicleListLoading,
    } = useGetVehicleListByUserId(customerId || recordFoundByPhone?.id, {
        enabled:
            ((customerType === USER_TYPES.SHOP_CREATED || customerType === USER_TYPES.APEX_AUTO_APP) && !!customerId) ||
            (!!recordFoundByPhone?.found && !!recordFoundByPhone?.id && !recordFoundByPhone?.isShopImported),
    });

    useEffect(() => {
        if (vehicleListData?.length > 0) {
            setCustomerVehicles(vehicleListData);
        } else {
            setCustomerVehicles([]);
        }
    }, [vehicleListData]);

    // Set default country when countryList loads (only if not editing a customer)
    useEffect(() => {
        if (countryList && countryList.length > 0 && !customerId && !recordFoundByPhone?.found) {
            const selected = countryList.find((item) => item.default === true);
            if (selected) {
                setSelectedCountry(selected);
                setSelectedCountrySecondary(selected);
            }
        }
    }, [countryList, customerId, recordFoundByPhone?.found]);

    // Helper function to populate form fields from customer data
    const populateFormFromCustomerData = useCallback(
        (customerData) => {
            if (!customerData) return;

            // Basic information
            setValue('firstName', customerData.firstName || '');
            setValue('lastName', customerData.lastName || '');
            setValue('company', customerData.company || '');

            // Primary phone number - format with dashes if needed
            const primaryPhone = customerData.phoneNumber || '';
            const formattedPrimaryPhone = primaryPhone.includes('-') ? primaryPhone : inputFormatPhoneNumber(primaryPhone);
            setValue('phoneNumber', formattedPrimaryPhone);
            const primaryPhoneType = customerData.primaryPhoneNumberType || 'mobile';
            setValue('primaryPhoneNumberType', primaryPhoneType);
            setSelectedPhoneTypes((prev) => ({
                ...prev,
                primary: primaryPhoneType,
            }));

            // Set primary phone country if available
            if (countryList && countryList.length > 0) {
                if (customerData.phoneNumberCountry) {
                    // Handle both object and string formats
                    const countryId =
                        typeof customerData.phoneNumberCountry === 'object' ? customerData.phoneNumberCountry._id : customerData.phoneNumberCountry;
                    const primaryCountry = countryList.find((item) => item._id === countryId);
                    if (primaryCountry) {
                        setSelectedCountry(primaryCountry);
                    } else {
                        // If country ID not found in list, use default
                        const selected = countryList.find((item) => item.default === true);
                        if (selected) {
                            setSelectedCountry(selected);
                        }
                    }
                } else {
                    // Fallback to default if no country is set
                    const selected = countryList.find((item) => item.default === true);
                    if (selected) {
                        setSelectedCountry(selected);
                    }
                }
            }

            // Secondary phone number
            if (customerData.secondaryPhoneNumber) {
                const secondaryPhone = customerData.secondaryPhoneNumber;
                const formattedSecondaryPhone = secondaryPhone.includes('-') ? secondaryPhone : inputFormatPhoneNumber(secondaryPhone);
                setValue('secondaryPhoneNumber', formattedSecondaryPhone);
                const secondaryPhoneType = customerData.secondaryPhoneNumberType || 'mobile';
                setValue('secondaryPhoneNumberType', secondaryPhoneType);
                setSelectedPhoneTypes((prev) => ({
                    ...prev,
                    secondary: secondaryPhoneType,
                }));

                // Set secondary phone country if available
                if (countryList && countryList.length > 0) {
                    if (customerData.secondaryPhoneNumberCountry) {
                        // Handle both object and string formats
                        const secondaryCountryId =
                            typeof customerData.secondaryPhoneNumberCountry === 'object'
                                ? customerData.secondaryPhoneNumberCountry._id
                                : customerData.secondaryPhoneNumberCountry;
                        const secondaryCountry = countryList.find((item) => item._id === secondaryCountryId);
                        if (secondaryCountry) {
                            setSelectedCountrySecondary(secondaryCountry);
                        } else {
                            // If country ID not found in list, use default
                            const selected = countryList.find((item) => item.default === true);
                            if (selected) {
                                setSelectedCountrySecondary(selected);
                            }
                        }
                    } else {
                        // Fallback to default if no country is set
                        const selected = countryList.find((item) => item.default === true);
                        if (selected) {
                            setSelectedCountrySecondary(selected);
                        }
                    }
                }
            } else {
                // Clear secondary phone if not present
                setValue('secondaryPhoneNumber', '');
                // Set default country for secondary when no secondary phone
                if (countryList && countryList.length > 0) {
                    const selected = countryList.find((item) => item.default === true);
                    if (selected) {
                        setSelectedCountrySecondary(selected);
                    }
                }
            }

            // Email addresses
            setValue('email', customerData.email || '');
            setValue('secondaryEmail', customerData.secondaryEmail || '');

            // Address fields
            setValue('streetAddress', customerData.streetAddress || '');

            // City - handle both string and object formats
            if (customerData.city) {
                if (typeof customerData.city === 'string') {
                    setValue('city', customerData.city);
                } else if (customerData.city._id) {
                    setValue('city', customerData.city._id);
                }
            } else {
                setValue('city', '');
            }

            // Province - handle both string and object formats
            if (customerData.province) {
                if (typeof customerData.province === 'string') {
                    setValue('province', customerData.province);
                } else if (customerData.province._id) {
                    setValue('province', customerData.province._id);
                }
            } else {
                setValue('province', '');
            }

            setValue('postalCode', formatPostalCode(customerData.postalCode, customerData?.country || selectedCountry));
            setValue('taxExemptionNumber', customerData.taxExemptionNumber || '');

            // Contact preference
            setValue('contactPrefrence', customerData.contactPrefrence || '');

            // Membership program
            setValue('membershipProgram', customerData.membershipProgram?._id || 'none');

            // Fleet customer status
            setValue('isFleetCustomer', !!customerData.isFleetCustomer);
        },
        [countryList, setValue, setSelectedPhoneTypes, setSelectedCountry, setSelectedCountrySecondary]
    );

    useEffect(() => {
        if (from !== 'invoice_email') return;
        if (!openModal) return;
        if (activeStep !== 0) return;

        setTimeout(() => {
            emailRef.current?.focus();
        }, 2000);
    }, [from, openModal, activeStep]);

    // Set customer detail when data loads (for edit mode - shop created)
    useEffect(() => {
        if (customerDetailData && openModal && countryList && countryList.length > 0) {
            populateFormFromCustomerData(customerDetailData);
        }
    }, [openModal, customerDetailData, countryList, populateFormFromCustomerData]);

    // Set customer detail for shop_imported when data loads (for edit mode)
    useEffect(() => {
        if (shopImportedCustomerDetail && openModal && countryList && countryList.length > 0) {
            setRecordFoundByPhone({
                id: shopImportedCustomerDetail._id,
                found: true,
                isFleetCustomer: shopImportedCustomerDetail.isFleetCustomer,
                isShopImported: true,
            });
            if (shopImportedCustomerDetail?.vehicle?.length > 0) {
                setCustomerVehicles(shopImportedCustomerDetail.vehicle);
            }
            populateFormFromCustomerData(shopImportedCustomerDetail);
        }
    }, [openModal, shopImportedCustomerDetail, countryList, populateFormFromCustomerData]);

    // Ensure default country is set when countryList loads and we're editing but no country was found
    useEffect(() => {
        if (countryList && countryList.length > 0 && (customerId || recordFoundByPhone?.found)) {
            // Only set default if country is not already set
            if (!selectedCountry) {
                const selected = countryList.find((item) => item.default === true);
                if (selected) {
                    setSelectedCountry(selected);
                }
            }
            if (!selectedCountrySecondary) {
                const selected = countryList.find((item) => item.default === true);
                if (selected) {
                    setSelectedCountrySecondary(selected);
                }
            }
        }
    }, [countryList, customerId, recordFoundByPhone?.found, selectedCountry, selectedCountrySecondary]);

    // Handle Country Change for Primary Phone
    const handleCountryChange = (event) => {
        const selected = countryList.find((item) => item._id === event.target.value);
        setSelectedCountry(selected);
        if (triggerSelection === null) {
            setTriggerSelection(true);
        } else {
            setTriggerSelection(!triggerSelection);
        }

        // Auto-trigger phone check if 10 digits are already entered
        const phoneNumber = getValues('phoneNumber');
        if (phoneNumber && removeDashFromPhoneNumber(phoneNumber).length === 10) {
            const cleanPhoneNumber = removeDashFromPhoneNumber(phoneNumber);
            const originalPhone = customerDetailData?.phoneNumber ? removeDashFromPhoneNumber(customerDetailData.phoneNumber) : null;
            const originalCountryId = customerDetailData?.phoneNumberCountry?._id || customerDetailData?.phoneNumberCountry || null;

            // Skip mutation if phone number and country match original values
            if (customerId && originalPhone === cleanPhoneNumber && originalCountryId === selected?._id) {
                return;
            }

            setTimeout(() => {
                checkPhoneMutation.mutate({
                    phoneNumber: cleanPhoneNumber,
                    countryCode: selected?.dial_code || '+1',
                    phoneNumberCountry: selected?._id,
                });
            }, 100);
        }
    };

    // Handle Country Change for Secondary Phone
    const handleCountryChangeSecondary = (event) => {
        const selected = countryList.find((item) => item._id === event.target.value);
        setSelectedCountrySecondary(selected);
    };

    // Handle Close Modal
    const handleCloseModal = () => {
        setActiveStep(0);
        setCompleted({});
        reset();
        const config = shopConfigData?.config || shopConfigData;
        const defaultWelcome = config?.defaultSendWelcomeEmailToCustomer !== undefined ? Boolean(config.defaultSendWelcomeEmailToCustomer) : true;
        setSendWelcomeEmailToCustomer(defaultWelcome);
        setRecordFoundByPhone({
            id: '',
            found: false,
            isFleetCustomer: false,
            isShopImported: false,
        });
        setShowCustomerFoundMessage(false);
        setIsAddingNewVehicle(false);
        setEditVehicleData(null);
        setSelectedVehicleId(null);
        onClose();
        setTimeout(() => {
            queryClient.removeQueries({ queryKey: customerKeys.createdDetail(customerId || recordFoundByPhone?.id) });
            queryClient.removeQueries({ queryKey: customerKeys.importedDetail(customerId || recordFoundByPhone?.id) });
            queryClient.removeQueries({ queryKey: vehicleKeys.list(customerId || recordFoundByPhone?.id) });
        }, 100);
    };

    // Handle phone number check
    const handlePhoneNumberCheck = () => {
        const phoneNumber = getValues('phoneNumber');
        const cleanPhoneNumber = removeDashFromPhoneNumber(phoneNumber);

        if (!phoneNumber || cleanPhoneNumber.length !== 10) {
            return;
        }
        const data = customerDetailData || shopImportedCustomerDetail;

        const originalPhone = data?.phoneNumber ? removeDashFromPhoneNumber(data.phoneNumber) : null;
        const originalCountryId = data?.phoneNumberCountry?._id || data?.phoneNumberCountry || null;

        // Skip mutation if phone number and country match original values
        if (customerId && originalPhone === cleanPhoneNumber && originalCountryId === selectedCountry?._id) {
            if (shopImportedCustomerDetail?._id) {
                setRecordFoundByPhone({
                    id: shopImportedCustomerDetail._id,
                    found: true,
                    isFleetCustomer: shopImportedCustomerDetail.isFleetCustomer,
                    isShopImported: true,
                });
                if (shopImportedCustomerDetail?.vehicle?.length > 0) {
                    setCustomerVehicles(shopImportedCustomerDetail.vehicle);
                }
            }
            return;
        }

        checkPhoneMutation.mutate({
            phoneNumber: cleanPhoneNumber,
            countryCode: selectedCountry?.dial_code || '+1',
            phoneNumberCountry: selectedCountry?._id,
        });
    };

    // Watch phone number and auto-trigger check when 10 digits are entered
    const watchPhoneNumber = watch('phoneNumber');
    useEffect(() => {
        if (watchPhoneNumber && removeDashFromPhoneNumber(watchPhoneNumber).length === 10) {
            // Debounce to avoid multiple calls
            const timer = setTimeout(() => {
                handlePhoneNumberCheck();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [watchPhoneNumber]);

    // Handle Submit Modal
    const handleSubmitModal = (data) => {
        // Construct payload matching the old modal structure
        let payload = {
            vehicle: [],
            countryCode: selectedCountry?.dial_code || '+1',
            phoneNumberCountry: selectedCountry?._id,
            isFleetCustomer: data.isFleetCustomer || false,
        };

        // Basic fields
        if (data.firstName) payload.firstName = data.firstName.trim();
        if (data.lastName) payload.lastName = data.lastName.trim();
        payload.company = data.company || '';

        // Phone numbers
        payload.phoneNumber = data.phoneNumber;
        payload.primaryPhoneNumberType = data?.primaryPhoneNumberType;

        // Always include secondary phone number fields (even if empty)
        if (data?.secondaryPhoneNumber) {
            payload.secondaryPhoneNumber = data.secondaryPhoneNumber;
            payload.secondaryPhoneNumberType = data?.secondaryPhoneNumberType;
            payload.secondaryPhoneNumberCountryCode = selectedCountrySecondary?.dial_code;
            payload.secondaryPhoneNumberCountry = selectedCountrySecondary?._id;
        }

        // Only include secondary phone country if secondary phone number exists
        if (data.secondaryPhoneNumber && data.secondaryPhoneNumber !== '') {
            payload.secondaryPhoneNumberCountryCode = selectedCountrySecondary?.dial_code;
            payload.secondaryPhoneNumberCountry = selectedCountrySecondary?._id;
        }

        // Always include email fields (even if empty string to remove from API)
        payload.email = data?.email || '';
        payload.secondaryEmail = data?.secondaryEmail || '';

        // Contact preference
        if (data?.contactPrefrence) {
            payload.contactPrefrence = data?.contactPrefrence;
        }

        // Address
        payload.streetAddress = data?.streetAddress || '';
        payload.city = data?.city || '';
        payload.province = data?.province || '';
        payload.postalCode = data?.postalCode || '';
        payload.taxExemptionNumber = data?.taxExemptionNumber ? data.taxExemptionNumber.trim() : '';

        // Always include membership program (empty string if none to remove from API)
        if (data?.membershipProgram && data?.membershipProgram !== '' && data?.membershipProgram !== 'none') {
            payload.membershipProgram = data.membershipProgram;
        } else {
            payload.membershipProgram = '';
        }

        //Create payload and call shop created mutation
        if (!recordFoundByPhone?.isShopImported && (customerType === USER_TYPES.SHOP_CREATED || customerType === USER_TYPES.APEX_AUTO_APP)) {
            if ((recordFoundByPhone?.found || customerId) && selectedVehicleId) {
                // If customer exists and a vehicle is selected, include the selected vehicle ID
                // payload.vehicle = [{ _id: selectedVehicleId }];
            } else {
                // Vehicle data - ONLY if adding new vehicle or if customer is new
                if (!recordFoundByPhone?.found) {
                    let vehiclePayload = {
                        vin: data.vin || undefined,
                        year: data.year ? data.year + '' : undefined,
                        vehicleMakeId: data.vehicleMakeId,
                        vehicleModelId: data.vehicleModelId,
                        vehicleModelTrimId: data.vehicleModelTrimId || undefined,
                        licenseNumber: data.licenseNumber || undefined,
                        provinceId: data.provinceId || undefined,
                        fleetUnitNumber: data.isFleetCustomer ? data.fleetUnitNumber : undefined,
                    };

                    // Filter undefined values from vehicle payload
                    const filteredVehicle = filterUndefined(vehiclePayload);
                    if (Object.keys(filteredVehicle).length > 0) {
                        payload.vehicle = [filteredVehicle];
                    }
                }
            }

            // Add welcome email flag only for new customers
            if (!customerId && !recordFoundByPhone?.found) {
                payload.sendWelcomeEmailToCustomer = sendWelcomeEmailToCustomer;
            }
            // Call appropriate mutation
            if (customerId || recordFoundByPhone?.found) {
                // Update existing customer
                updateCustomerMutation.mutate({
                    id: customerId || recordFoundByPhone.id,
                    payload: payload,
                });
            } else {
                // Create new customer
                createCustomerMutation.mutate(payload);
            }
        }

        //Create payload and call shop imported mutation
        if (customerType === USER_TYPES.SHOP_IMPORTED || recordFoundByPhone?.isShopImported || customerType === USER_TYPES.WALK_IN) {
            delete payload.vehicle;
            if (payload.membershipProgram === '') {
                delete payload.membershipProgram;
            }
            const _id = customerId || (recordFoundByPhone?.isShopImported && recordFoundByPhone?.id);
            payload.importShopCustomerId = _id;

            if (_id) {
                // Update existing customer for shop_imported; pass selectedVehicleId so onSuccess gets submit-time value (avoids stale closure)
                updateCustomerImportedMutation.mutate({
                    id: _id,
                    payload: payload,
                    selectedVehicleId: selectedVehicleId ?? undefined,
                });
            }
        }
    };

    // Handle form validation error
    const onInvalid = (errors) => {
        console.error('Form validation errors:', errors);
        const errorFields = Object.keys(errors);
        if (errorFields.length > 0) {
            const firstErrorField = errorFields[0];

            // Define fields for each step to switch if necessary
            const step0Fields = [
                'firstName',
                'lastName',
                'phoneNumber',
                'primaryPhoneNumberType',
                'email',
                'secondaryEmail',
                'secondaryPhoneNumber',
                'company',
                'contactPrefrence',
            ];
            const step1Fields = ['streetAddress', 'city', 'province', 'postalCode', 'membershipProgram', 'isFleetCustomer'];

            let targetStep = activeStep;
            if (step0Fields.includes(firstErrorField)) {
                targetStep = 0;
            } else if (step1Fields.includes(firstErrorField)) {
                targetStep = 1;
            } else {
                targetStep = 2; // Vehicle details or selection
            }

            if (targetStep !== activeStep) {
                setActiveStep(targetStep);
            }

            // Small delay to allow step transition and rendering before focus/scroll
            setTimeout(() => {
                setFocus(firstErrorField);
                const element = document.getElementsByName(firstErrorField)[0] || document.querySelector(`[name="${firstErrorField}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    };

    // Handle Next Button Click
    const handleNext = async () => {
        if (isEditCustomerMode) {
            const isStep0Valid = await trigger(
                ['firstName', 'lastName', 'phoneNumber', 'primaryPhoneNumberType', 'email', 'secondaryEmail', 'secondaryPhoneNumber'],
                { shouldFocus: true }
            );

            // Manual scroll for trigger if it failed
            if (!isStep0Valid) {
                const step0Fields = ['firstName', 'lastName', 'phoneNumber', 'primaryPhoneNumberType', 'email', 'secondaryEmail', 'secondaryPhoneNumber'];
                const firstError = step0Fields.find((key) => getFieldState(key).error);
                if (firstError) {
                    // These fields live on the Customer Details tab - show it, or the error is invisible
                    setActiveStep(0);
                    setFocus(firstError);
                    setTimeout(() => {
                        const element = document.getElementsByName(firstError)[0] || document.querySelector(`[name="${firstError}"]`);
                        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                }
            }

            const primaryEmail = getValues('email');
            const secondaryEmail = getValues('secondaryEmail');

            if (primaryEmail && secondaryEmail && primaryEmail.toLowerCase() === secondaryEmail.toLowerCase()) {
                setActiveStep(0);
                setError('secondaryEmail', {
                    type: 'manual',
                    message: "You've entered the same email for both primary and secondary.",
                });
                setFocus('secondaryEmail');
                setTimeout(() => {
                    const element = document.getElementsByName('secondaryEmail')[0] || document.querySelector('[name="secondaryEmail"]');
                    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
                return;
            }

            const watchedPostal = getValues('postalCode');
            let isPostalValid = true;

            if (watchedPostal && watchedPostal.length > 0) {
                if (!isPostalCodeValid(watchedPostal, selectedCountry)) {
                    // Postal code lives on the Additional Details tab - show it, or the error is invisible
                    setActiveStep(1);
                    setError('postalCode', {
                        type: 'manual',
                        message: getPostalCodeError(selectedCountry),
                    });
                    setTimeout(() => {
                        setFocus('postalCode');
                        const element = document.getElementsByName('postalCode')[0] || document.querySelector('[name="postalCode"]');
                        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                    isPostalValid = false;
                }
            } else {
                clearErrors('postalCode');
            }

            if (isStep0Valid && isPostalValid) {
                handleSubmit(handleSubmitModal, onInvalid)();
            }
            return;
        }

        const isLastStep = activeStep === steps.length - 1;

        if (activeStep === 0) {
            // Step 1: Customer Details validation
            const isValid = await trigger(
                ['firstName', 'lastName', 'phoneNumber', 'primaryPhoneNumberType', 'email', 'secondaryEmail', 'secondaryPhoneNumber'],
                { shouldFocus: true }
            );

            if (isValid) {
                // Check if primary and secondary emails are the same
                const primaryEmail = getValues('email');
                const secondaryEmail = getValues('secondaryEmail');

                if (primaryEmail && secondaryEmail && primaryEmail.toLowerCase() === secondaryEmail.toLowerCase()) {
                    setError('secondaryEmail', {
                        type: 'manual',
                        message: "You've entered the same email for both primary and secondary.",
                    });
                    setFocus('secondaryEmail');
                    setTimeout(() => {
                        const element = document.getElementsByName('secondaryEmail')[0] || document.querySelector('[name="secondaryEmail"]');
                        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                    return; // Stop navigation
                }

                if (isLastStep) {
                    // If this is the last step, submit the form
                    handleSubmit(handleSubmitModal, onInvalid)();
                } else {
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                    setCompleted({ ...completed, 0: true });
                }
            } else {
                // Manual scroll for trigger if it failed
                const step0Fields = ['firstName', 'lastName', 'phoneNumber', 'primaryPhoneNumberType', 'email', 'secondaryEmail', 'secondaryPhoneNumber'];
                const firstError = step0Fields.find((key) => getFieldState(key).error);
                if (firstError) {
                    setFocus(firstError);
                    setTimeout(() => {
                        const element = document.getElementsByName(firstError)[0] || document.querySelector(`[name="${firstError}"]`);
                        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                }
            }
        } else if (activeStep === 1) {
            // Step 2: Additional Details validation
            const watchedPostal = getValues('postalCode');

            // Only validate postal code format if it has a value
            if (watchedPostal && watchedPostal.length > 0) {
                if (!isPostalCodeValid(watchedPostal, selectedCountry)) {
                    setError('postalCode', {
                        type: 'manual',
                        message: getPostalCodeError(selectedCountry),
                    });
                    setFocus('postalCode');
                    setTimeout(() => {
                        const element = document.getElementsByName('postalCode')[0] || document.querySelector('[name="postalCode"]');
                        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 100);
                    return; // Stop navigation if postal code format is invalid
                }
            }

            // Clear any postal code errors if validation passes
            clearErrors('postalCode');

            // Proceed to next step or submit
            if (isLastStep) {
                // If this is the last step, submit the form
                handleSubmit(handleSubmitModal, onInvalid)();
            } else {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                setCompleted({ ...completed, 1: true });
            }
        } else {
            // Step 3: Vehicle Details - Validation for Shop Imported Customers
            if (activeStep === 2 && recordFoundByPhone?.isShopImported && customerVehicles && customerVehicles.length > 0) {
                const isLicensePlateRequired = shopConfigData?.config?.isLicensePlateRequired;

                const hasValidVehicle = customerVehicles.some((vehicle) => validateVehicle(vehicle, isLicensePlateRequired));

                if (!hasValidVehicle) {
                    toast.error('Please add required vehicle details to continue');
                    return;
                }
            }

            // Step 3: Vehicle Details - Submit form (only if 3 steps)
            handleSubmit(handleSubmitModal, onInvalid)();
        }
    };
    // Handle Back Button Click
    const handleBack = () => {
        const newCompleted = { ...completed };
        // Remove completion status for current step and all subsequent steps
        for (let i = activeStep - 1; i < steps.length; i++) {
            delete newCompleted[i];
        }

        setCompleted(newCompleted);
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
    const handleAddVehicle = () => {
        setEditVehicleData(null);
        setIsAddingNewVehicle(true);
    };

    const handleEditVehicle = (vehicle) => {
        if (vehicle && vehicle._id) {
            setEditVehicleData(vehicle);
            setIsAddingNewVehicle(true);
        }
    };

    const handleDeleteVehicle = (vehicle) => {
        if (vehicle && vehicle._id) {
            setDeleteVehicleData(vehicle);
            setIsDeleteModal(true);
        }
    };

    // Function to get modal title based on customer type, step, and conditions
    const getModalTitle = () => {
        // SHOP_IMPORTED: Same title for all steps
        if (customerType === USER_TYPES.SHOP_IMPORTED || customerType === USER_TYPES.WALK_IN) {
            return 'Confirm Customer Information';
        }

        // SHOP_CREATED: Different titles based on step
        if (customerType === USER_TYPES.SHOP_CREATED) {
            // If opened from profile, always show "Edit Customer Details"
            if (fromProfile && customerId) {
                return 'Edit Customer Details';
            }
            if (activeStep === 0) {
                // Step 0: "Edit Customer" if customerId is provided, otherwise "New Customer"
                return customerId ? 'Edit Customer' : 'New Customer';
            } else if (activeStep === 1) {
                // Step 1: "Additional Details"
                return 'Additional Details';
            } else if (activeStep === 2) {
                // Step 2: "Select Vehicle" if record found, otherwise "Add Vehicle"
                return recordFoundByPhone?.found ? 'Select Vehicle' : 'Add Vehicle';
            }
        }

        // Default fallback
        return 'New Customer';
    };

    return (
        <>
            <Modal
                open={openModal || openEditCustomer}
                onClose={(event, reason) => {
                    if (reason && reason === 'backdropClick') {
                        return;
                    }
                    handleCloseModal();
                }}
                aria-labelledby="parent-modal-title"
                aria-describedby="parent-modal-description"
            >
                <Box sx={styles.modalBox} className="modalBodyBoxContainer">
                    <ModalHeader
                        title={getModalTitle()}
                        onClose={handleCloseModal}
                        isLoading={
                            checkPhoneMutation.isPending ||
                            createCustomerMutation.isPending ||
                            updateCustomerMutation.isPending ||
                            updateCustomerImportedMutation.isPending ||
                            deleteCustomerMutation.isPending
                        }
                    />

                    <Container maxWidth={false} disableGutters style={styles.container}>
                        <form
                            onSubmit={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                // handleSubmit is handled by the Next/Save button
                            }}
                            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                        >
                            <Box sx={styles.gridContainer}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} display="flex" justifyContent="center">
                                        {isEditCustomerMode ? (
                                            <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
                                                <Tabs value={activeStep} onChange={(e, val) => setActiveStep(val)} aria-label="customer details tabs">
                                                    {steps.map((label, index) => (
                                                        <Tab key={label} label={label} />
                                                    ))}
                                                </Tabs>
                                            </Box>
                                        ) : (
                                            <Stepper nonLinear activeStep={activeStep} sx={styles.stepperContainer}>
                                                {steps.map((label, index) => (
                                                    <Step key={label} completed={completed[index]}>
                                                        <StepButton
                                                            color="inherit"
                                                            style={{
                                                                paddingTop: '0px',
                                                                paddingBottom: '0px',
                                                            }}
                                                            onClick={() => {
                                                                // Allow navigation to previous steps or the current step
                                                                // Allow navigation to any completed step
                                                                // Disallow navigation to future, uncompleted steps
                                                                if (index < activeStep || completed[index]) {
                                                                    const newCompleted = { ...completed };

                                                                    // Remove completion status for clicked step and all subsequent steps
                                                                    for (let i = index; i < steps.length; i++) {
                                                                        delete newCompleted[i];
                                                                    }

                                                                    setCompleted(newCompleted);
                                                                    setActiveStep(index);
                                                                }
                                                            }}
                                                        >
                                                            {label}
                                                        </StepButton>
                                                    </Step>
                                                ))}
                                            </Stepper>
                                        )}
                                    </Grid>

                                    {/* Keep all steps mounted but hide inactive ones to preserve form state */}
                                    <Grid item xs={12} style={{ display: activeStep === 0 ? 'block' : 'none' }}>
                                        <CustomerDetailsForm
                                            control={control}
                                            errors={errors}
                                            setValue={setValue}
                                            phoneInputRef={phoneInputRef}
                                            emailRef={emailRef}
                                            selectedCountry={selectedCountry}
                                            selectedCountrySecondary={selectedCountrySecondary}
                                            countryList={countryList || []}
                                            handleCountryChange={handleCountryChange}
                                            handleCountryChangeSecondary={handleCountryChangeSecondary}
                                            showSendWelcomeEmailToCustomer={!customerId && !recordFoundByPhone.found}
                                            sendWelcomeEmailToCustomer={sendWelcomeEmailToCustomer}
                                            setSendWelcomeEmailToCustomer={setSendWelcomeEmailToCustomer}
                                            setError={setError}
                                            clearErrors={clearErrors}
                                            selectedPhoneTypes={selectedPhoneTypes}
                                            setSelectedPhoneTypes={setSelectedPhoneTypes}
                                            handlePhoneNumberCheck={handlePhoneNumberCheck}
                                            isCheckingPhone={checkPhoneMutation.isPending}
                                            showCustomerFoundMessage={showCustomerFoundMessage}
                                            setShowCustomerFoundMessage={setShowCustomerFoundMessage}
                                        />
                                    </Grid>

                                    <Grid item xs={12} style={{ display: activeStep === 1 ? 'block' : 'none' }}>
                                        <AdditionalDetailsForm
                                            control={control}
                                            errors={errors}
                                            setValue={setValue}
                                            setError={setError}
                                            clearErrors={clearErrors}
                                            watch={watch}
                                            discountList={discountList || []}
                                            cityList={cityList}
                                            provinceList={provinceList}
                                            selectedCountry={selectedCountry}
                                            setValueinForm={setValue}
                                        />
                                    </Grid>

                                    <Grid item xs={12} style={{ display: activeStep === 2 ? 'block' : 'none' }}>
                                        {customerId || recordFoundByPhone.found ? (
                                            <VehicleSelectionForm
                                                watch={watch}
                                                isRecordFoundIsShopImported={recordFoundByPhone?.isShopImported}
                                                isLoading={isVehicleListLoading}
                                                vehicles={customerVehicles || []}
                                                onAddVehicle={handleAddVehicle}
                                                onEditVehicle={handleEditVehicle}
                                                onDeleteVehicle={handleDeleteVehicle}
                                                onSelectVehicle={(vehicleId) => {
                                                    setSelectedVehicleId(vehicleId);
                                                }}
                                                selectedVehicleId={selectedVehicleId}
                                                customerId={customerId || recordFoundByPhone.id}
                                            />
                                        ) : (
                                            <VehicleDetailsForm
                                                control={control}
                                                errors={errors}
                                                watch={watch}
                                                setValue={setValue}
                                                getValues={getValues}
                                                clearErrors={clearErrors}
                                                setError={setError}
                                                provinceCombinedList={provinceCombinedList || []}
                                                config={shopConfigData?.config}
                                                customerType={customerType}
                                                customerId={customerId || recordFoundByPhone.id}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Footer */}
                            <ModalFooter
                                customerType={customerType}
                                fromProfile={fromProfile}
                                isEditMode={isEditCustomerMode}
                                onDelete={() => setIsCustomerDeleteModal(true)}
                                onClose={handleCloseModal}
                                onNext={handleNext}
                                onBack={handleBack}
                                activeStep={activeStep}
                                steps={steps}
                                isLoading={
                                    customerDetailLoading ||
                                    checkPhoneMutation.isPending ||
                                    createCustomerMutation.isPending ||
                                    updateCustomerMutation.isPending ||
                                    updateCustomerImportedMutation.isPending ||
                                    deleteCustomerMutation.isPending
                                }
                            />
                        </form>
                    </Container>
                </Box>
            </Modal>

            {isAddingNewVehicle && (
                <Suspense fallback={<CircularProgress />}>
                    <VehicleModalV4
                        open={true}
                        enableLicenseDecoding={false}
                        mode={
                            recordFoundByPhone?.isShopImported || (editVehicleData?.source === 'shop_imported' && !editVehicleData?.isConverted)
                                ? 'update_shop_imported_vehicle'
                                : editVehicleData
                                  ? 'update_shop_created_vehicle'
                                  : 'create'
                        }
                        customerId={customerId || recordFoundByPhone.id}
                        vehicleId={editVehicleData?._id}
                        vehicleData={
                            editVehicleData
                                ? {
                                      _id: editVehicleData?._id,
                                      vin: editVehicleData?.vin || '',
                                      year: editVehicleData?.year || editVehicleData?.vehicleYear || '',
                                      vehicleMakeId: editVehicleData?.vehicleMake?._id,
                                      vehicleModelId: editVehicleData?.vehicleModel?._id,
                                      vehicleModelTrimId: editVehicleData?.vehicleModelTrim?._id,
                                      licenseNumber: editVehicleData?.licenseNumber || editVehicleData?.license || '',
                                      provinceId: editVehicleData?.provinceId || editVehicleData?.province?._id,
                                      fleetUnitNumber: editVehicleData?.fleetUnitNumber || '',
                                  }
                                : null
                        }
                        isFleetCustomer={recordFoundByPhone?.isFleetCustomer || getValues('isFleetCustomer') || false}
                        onClose={() => {
                            setIsAddingNewVehicle(false);
                            setEditVehicleData(null);
                        }}
                        onSuccess={(updatedVehicle) => {
                            if (recordFoundByPhone?.isShopImported || (editVehicleData?.source === 'shop_imported' && !editVehicleData?.isConverted)) {
                                // Manual state update for shop imported vehicles or customers
                                setCustomerVehicles((prev) => {
                                    const exists = prev.find((v) => v._id === updatedVehicle._id);
                                    if (exists) {
                                        return prev.map((v) => (v._id === updatedVehicle._id ? updatedVehicle : v));
                                    } else {
                                        return [...prev, updatedVehicle];
                                    }
                                });
                            } else {
                                // Refresh vehicle list after successful add/update
                                refetchVehicles();
                            }
                            setIsAddingNewVehicle(false);
                            setEditVehicleData(null);
                        }}
                    />
                </Suspense>
            )}

            <CommonDialog
                loader={deleteVehicleMutation.isPending || deleteImportedVehicleMutation.isPending}
                open={isDeleteModal}
                onClose={() => {
                    setDeleteVehicleData(null);
                    setIsDeleteModal(false);
                }}
                onSave={() => {
                    if (recordFoundByPhone?.isShopImported || (deletevehicleData?.source === 'shop_imported' && !deletevehicleData?.isConverted)) {
                        deleteImportedVehicleMutation.mutate(deletevehicleData._id);
                    } else {
                        deleteVehicleMutation.mutate(deletevehicleData._id);
                    }
                }}
                dialogTitle="Delete Vehicle"
                dialogSubtitle="Are you sure you want to delete this vehicle?"
            />

            <CommonDialog
                loader={deleteCustomerMutation.isPending}
                open={isCustomerDeleteModal}
                onClose={() => {
                    setIsCustomerDeleteModal(false);
                }}
                onSave={async () => {
                    deleteCustomerMutation.mutate(customerId);
                }}
                dialogTitle="Delete Customer?"
                dialogSubtitle="Are you sure you want to delete this customer?"
            />

            {/* Full-screen loading dialog for create/update operations and customer detail loading */}
            <Dialog
                fullScreen={false}
                open={
                    customerDetailLoading ||
                    shopImportedCustomerDetailLoading ||
                    createCustomerMutation.isPending ||
                    updateCustomerMutation.isPending ||
                    updateCustomerImportedMutation.isPending ||
                    deleteCustomerMutation.isPending
                }
            >
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddCustomerModalV4;
